import {
  type DayOfWeek,
  dayOfWeekSchema,
  type Schedule,
  type TimeOfDay,
  timeWindowSchema,
} from '@kidshub/contracts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '../ui/kit.js';

/**
 * Adds and removes permitted hours, one day at a time.
 *
 * Windows may not wrap past midnight — the contract refuses them, and an
 * overnight allowance is expressed as two windows on two days. That is not a
 * limitation to work around here: it keeps every comparison in the policy
 * engine a simple one.
 *
 * A day with no windows permits nothing. The screen says so rather than letting
 * an empty row read as "no restriction".
 */

interface ScheduleEditorProps {
  schedule: Schedule;
  onChange: (schedule: Schedule) => void;
}

const DEFAULT_START = '09:00';
const DEFAULT_END = '12:00';

export const ScheduleEditor = ({ schedule, onChange }: ScheduleEditorProps) => {
  const { t } = useTranslation();
  const [openDay, setOpenDay] = useState<DayOfWeek | null>(null);
  const [start, setStart] = useState(DEFAULT_START);
  const [end, setEnd] = useState(DEFAULT_END);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const windowsFor = (day: DayOfWeek) => schedule.windowsByDay[day] ?? [];

  const replaceDay = (day: DayOfWeek, windows: { start: TimeOfDay; end: TimeOfDay }[]) => {
    onChange({ windowsByDay: { ...schedule.windowsByDay, [day]: windows } });
  };

  const addWindow = (day: DayOfWeek) => {
    const candidate = timeWindowSchema.safeParse({ start, end });
    if (!candidate.success) {
      // The one rule worth stating in the interface: a window ends after it
      // starts, and an overnight span is two windows.
      setErrorKey('screenTime.windowOrderError');
      return;
    }

    setErrorKey(null);
    replaceDay(day, [...windowsFor(day), candidate.data]);
    setOpenDay(null);
  };

  const removeWindow = (day: DayOfWeek, index: number) => {
    replaceDay(
      day,
      windowsFor(day).filter((_, position) => position !== index),
    );
  };

  return (
    <div className="sched">
      {dayOfWeekSchema.options.map((day) => (
        <div className="sched-day" key={day}>
          <span className="wk-day">{t(`day.${day}.short`)}</span>

          <div className="sched-windows">
            {windowsFor(day).length === 0 ? (
              <span className="t-meta">{t('screenTime.dayClosed')}</span>
            ) : null}

            {windowsFor(day).map((window, index) => (
              <span className="window-chip num" key={`${window.start}-${window.end}`}>
                {window.start}–{window.end}
                <button
                  type="button"
                  onClick={() => removeWindow(day, index)}
                  aria-label={t('screenTime.removeWindow', {
                    day: t(`day.${day}.long`),
                    start: window.start,
                    end: window.end,
                  })}
                >
                  <Icon name="x" small />
                </button>
              </span>
            ))}

            {openDay === day ? (
              <span className="window-add">
                <input
                  type="time"
                  className="input"
                  value={start}
                  onChange={(event) => setStart(event.target.value)}
                  aria-label={t('screenTime.windowStart')}
                />
                <input
                  type="time"
                  className="input"
                  value={end}
                  onChange={(event) => setEnd(event.target.value)}
                  aria-label={t('screenTime.windowEnd')}
                />
                <Button small variant="secondary" onClick={() => addWindow(day)}>
                  {t('common.add')}
                </Button>
                <Button
                  small
                  variant="ghost"
                  onClick={() => {
                    setOpenDay(null);
                    setErrorKey(null);
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </span>
            ) : (
              <Button
                small
                variant="ghost"
                icon="plus"
                onClick={() => {
                  setOpenDay(day);
                  setErrorKey(null);
                }}
              >
                {t('screenTime.addWindow')}
              </Button>
            )}
          </div>
        </div>
      ))}

      {errorKey === null ? null : (
        <p className="help err" role="alert">
          {t(errorKey)}
        </p>
      )}
      <p className="help">{t('screenTime.scheduleNote')}</p>
    </div>
  );
};
